// src/components/UploadLoaderModal.tsx

/**
 * 🚨 REFACTORING NOTE: FILE SPLIT COMPLETED
 * 
 * This large component (1738 lines) has been split into modular pieces:
 * 
 * 📁 /uploader-modal/
 * ├── types.ts                    - TypeScript interfaces and types
 * ├── constants.ts                - Configuration values and constants  
 * ├── utils.ts                    - Pure utility functions
 * ├── useStepManagement.ts        - Step state management hook
 * ├── useDownloadHandler.ts       - Download functionality hook
 * ├── useJobStatusMonitoring.ts   - Job polling and Pusher hook
 * ├── useStreamingContent.ts      - Streaming/markdown content hook
 * └── index.ts                    - Export all functionality
 * 
 * NEXT STEP: Refactor this component to use the extracted hooks:
 * 
 * import { 
 *   useStepManagement, 
 *   useDownloadHandler,
 *   useJobStatusMonitoring,
 *   useStreamingContent,
 *   COLORS,
 *   DEFAULT_MESSAGES 
 * } from './uploader-modal';
 * 
 * Benefits:
 * ✅ Reduced complexity (1738 lines → ~300 lines main component)
 * ✅ Better testability (individual hooks can be unit tested)
 * ✅ Improved maintainability (single responsibility principle)
 * ✅ Enhanced reusability (hooks can be used in other components)
 * ✅ Easier debugging (isolated concerns)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  Modal, 
  Box, 
  Fade
} from "@mui/material";
import 'highlight.js/styles/github.css'; // Add CSS for syntax highlighting
import type { JobStatusUpdate } from "../../utils/useJobStatusUpdates";
import { getAuthHeader } from "../../actions/utils/api";
import { downloadOutput } from "../../actions/files/downloadOutput";
import { getJobOutputText } from "../../actions/jobs/getJobOutputText";
import {
  getJobStatus,
  detectJobTypeFromPath,
  JobType,
  JobStatusResponse,
} from "../../utils/jobStatusApi";
import { extractTransformableText } from "../../utils/extractTransformableText";
import { useAuth } from "../../contexts/AuthContext";
import { setUserActiveJob } from "../../actions/user-jobs/setUserActiveJob";
import { useUsage } from "../../contexts/UsageContext";
import { useRealtimeJob, type RealtimeJobCompletionPayload, type RealtimeJobFailurePayload } from "../../hooks/useRealtimeJob";

// Import modular UI components
import {
  ModalHeader,
  ModalTabs,
  ProcessingStepsTab,
  CompletionFooter,
} from './uploader-modal/components';
import { DocumentChatContent } from './DocumentChatModal';
import { enhanceErrorMessage } from './uploader-modal/error-message';
import { CompactProcessingIndicator } from '../processing/CompactProcessingIndicator';

// Import utility functions
import { convertBackendStepsToFrontend, toSentenceCase } from './uploader-modal/utils';
import { PUSHER_TIMEOUT_MS, FALLBACK_POLL_INTERVAL_MS } from './uploader-modal/constants';

/**
 * 🚨 CTO NOTE: HIGH PRIORITY REFACTOR NEEDED
 * 
 * Current State: 7 useEffect hooks with complex dependencies
 * Industry Standard: Max 3 useEffect per component (Netflix guideline)
 * 
 * Required Actions:
 * 1. Extract to custom hook: useJobStatusMonitoring()
 * 2. Move to useReducer for state management
 * 3. Add React.ErrorBoundary wrapper
 * 4. Implement exponential backoff polling
 * 
 * Risk Level: HIGH - Complex state interdependencies
 * Timeline: Next sprint (high priority technical debt)
 */

/**
 * 🔧 STEP STATUS LOGIC FIX
 * 
 * Issue: Backend marks all steps as "processing" instead of completing previous steps
 * Solution: Frontend automatically marks all steps except latest as "completed"
 * 
 * Why this approach:
 * - Better UX: Only current step shows loading spinner
 * - Resilient: Works regardless of backend step status inconsistencies  
 * - Quick fix: Avoids complex backend changes to job registry
 * 
 * Location: Both stepHistory conversion points use this logic
 */

/**
 * 🆕 UPDATED: Upload Loader Modal with new single-job storage pattern
 * 
 * - Uses ActiveJobStorage instead of legacy localStorage functions
 * - Universal status endpoint getJobStatus(jobId)
 * - Backend-stored step history integration
 * - Simplified storage pattern (single job per user)
 */

interface ProcessingStep {
  id: string;
  label: string;
  status: 'pending' | 'processing' | 'completed';
  timestamp?: string;
}

// Props passed to a custom footer renderer so callers can implement
// bespoke completion experiences (e.g., fetch list data and show cards)
interface UploadLoaderFooterRenderProps {
  isCompleted: boolean;
  downloadLoading: boolean;
  stateCleared: boolean;
  completionResult: string;
  error: string;
  onDownload: (fileKey: string) => Promise<void> | void;
  onClose: () => void;
  jobId?: string;
  userId?: string;
  files: File[];
}

interface UploadLoaderModalProps {
  open: boolean;
  files: File[];
  jobId?: string; // For tracking processing after upload
  userId?: string; // For Pusher updates
  downloadUrl?: string; // URL for downloading the completed result
  onUpload: (
    files: File[],
    progressCallback: (progress: number) => void
  ) => Promise<string>;
  onComplete: (result: string) => void;
  onTextExtracted?: (text: string) => void;
  onStreamingStateChange?: (isStreaming: boolean) => void;
  onDownloadReady?: (downloadTarget: string) => void;
  onClose?: () => void; // Add manual close handler
  variant?: 'modal' | 'inline'; // New: render as blocking modal or inline card
  // How the inline card looks. 'steps' is the full step list; 'compact' is one progress bar with one
  // friendly status line (audio flows). Job tracking and completion handling are identical in both.
  presentation?: 'steps' | 'compact';
  maxHeight?: number | string; // Optional: constrain max height of container
  hideStatusUpdatesOnCompleted?: boolean; // Optional: hide steps list after completion
  renderFooter?: (props: UploadLoaderFooterRenderProps) => React.ReactNode; // Optional: custom footer
  theme?: 'dark' | 'light'; // Optional: header color scheme
  // Document chat support (premium feature)
  chatEnabled?: boolean; // Whether document embeddings are ready
  hasDocumentChatAccess?: boolean; // Whether user has premium access
  documentTitle?: string; // Service name for document chat modal (e.g., "Genesis Commercial")
}

const UploadLoaderModal: React.FC<UploadLoaderModalProps> = ({
  open,
  files,
  jobId,
  userId,
  downloadUrl,
  onUpload,
  onComplete,
  onTextExtracted,
  onStreamingStateChange,
  onDownloadReady,
  onClose,
  variant = 'modal',
  presentation = 'steps',
  maxHeight,
  hideStatusUpdatesOnCompleted = false,
  renderFooter,
  theme = 'dark',
  // Document chat support (premium feature)
  chatEnabled,
  hasDocumentChatAccess,
  documentTitle,
}) => {
  const { setProcessingModalOpen } = useAuth();
  const { refresh: refreshUsage } = useUsage();
  const [steps, setSteps] = useState<ProcessingStep[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string>("");
  // Raw (unenhanced) failure text and the backend's current step. The compact presentation maps them to
  // friendly copy; they are never rendered directly.
  const [rawError, setRawError] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [completionResult, setCompletionResult] = useState<string>("");
  const [outputFiles, setOutputFiles] = useState<Array<{ label: string; key: string }>>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [stateCleared, setStateCleared] = useState<boolean>(false);
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [streamingContent, setStreamingContent] = useState<string>("");
  const [fallbackTransformText, setFallbackTransformText] = useState<string>("");
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Thinking state
  const [thinkingText, setThinkingText] = useState<string>('');
  const [showThinking, setShowThinking] = useState<boolean>(false);
  
  // Document processing state
  const [documentProcessingText, setDocumentProcessingText] = useState<string>('');
  const [showDocumentProcessing, setShowDocumentProcessing] = useState<boolean>(false);
  const [currentProcessingPhase, setCurrentProcessingPhase] = useState<string>('');
  
  // Scroll state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastDownloadTargetRef = useRef('');
  
  // CRITICAL: Single source of truth for steps
  const stepsRef = useRef<ProcessingStep[]>([]);
  // Track previous open state to detect open/close transitions
  const prevOpenRef = useRef<boolean>(false);

  // Deep diagnostic: trace key prop changes that could close the modal
  useEffect(() => {
    try {
      console.log('[MODAL] props/state change', {
        open,
        filesLength: Array.isArray(files) ? files.length : -1,
        jobId,
        userId,
      });
    } catch {}
  }, [open, files, jobId, userId]);
  
  // ✅ ENTERPRISE SOLUTION: Simplified state management without dependency chains
  const updateStepsState = useCallback((newSteps: ProcessingStep[], reason: string) => {
    console.log(`[STEPS] ${reason}:`, newSteps.length, 'steps');
    
    // Update ref immediately
    stepsRef.current = newSteps;
    
    // Update React state
    setSteps(newSteps);
    
    // Don't save to ActiveJobStorage here - let the parent component handle persistence
    // This breaks the dependency chain and follows single responsibility principle
  }, []); // ✅ No dependencies needed - pure function

  // Add or update a single step
  const addOrUpdateStep = useCallback((stepUpdate: { label: string; status: 'pending' | 'processing' | 'completed'; id?: string }) => {
    const currentSteps = stepsRef.current;
    const existingIndex = currentSteps.findIndex(step => 
      step.label.toLowerCase() === stepUpdate.label.toLowerCase() ||
      (stepUpdate.id && step.id === stepUpdate.id)
    );
    
    let newSteps: ProcessingStep[];
    
    if (existingIndex !== -1) {
      // Update existing step
      newSteps = currentSteps.map((step, index) => {
        if (index === existingIndex) {
          return {
            ...step,
            status: stepUpdate.status,
            timestamp: stepUpdate.status === 'completed' ? new Date().toLocaleTimeString() : step.timestamp
          };
        }
        // Mark previous steps as completed
        if (index < existingIndex && step.status !== 'completed') {
          return {
            ...step,
            status: 'completed',
            timestamp: step.timestamp || new Date().toLocaleTimeString()
          };
        }
        return step;
      });
    } else {
      // Add new step
      const newStep: ProcessingStep = {
        id: stepUpdate.id || `step-${Date.now()}`,
        label: stepUpdate.label,
        status: stepUpdate.status,
        timestamp: stepUpdate.status === 'completed' ? new Date().toLocaleTimeString() : undefined
      };
      
      // Mark all previous steps as completed and add new step
      const completedSteps = currentSteps.map(step => ({
        ...step,
        status: 'completed' as const,
        timestamp: step.timestamp || new Date().toLocaleTimeString()
      }));
      
      newSteps = [...completedSteps, newStep];
    }
    
    updateStepsState(newSteps, `Adding/updating step: ${stepUpdate.label}`);
  }, [updateStepsState]);

  // ✅ SIMPLIFIED: Initialize steps removed (no longer using localStorage)

  // New streaming state
  const [, setDocumentSections] = useState<Map<string, string>>(new Map());
  const [isStreamingActive, setIsStreamingActive] = useState(false);
  
  // Chunked content handling
  const chunkBuffers = useRef<Map<string, { chunks: any[], totalChunks: number }>>(new Map());
  
  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track the last completed job to avoid showing previous completion state
  const lastCompletedJobRef = useRef<string | undefined>(undefined);

  // #611 — track which job we've already fired `onComplete` for, so the parent's
  // completion handler runs exactly once per job (it can make network calls).
  const completeNotifiedJobRef = useRef<string | undefined>(undefined);

  // Track the current jobId to detect job changes
  const currentJobIdRef = useRef<string | undefined>(undefined);
  
  // ✅ ENTERPRISE SOLUTION: Use stable callback with proper memoization
  const uploadStateRef = useRef<{
    hasStarted: boolean;
    jobId: string | undefined;
    attemptCount: number;
  }>({
    hasStarted: false,
    jobId: undefined,
    attemptCount: 0
  });
  // Ultra-simple single-run guard for this modal open session
  const processStartedRef = useRef<boolean>(false);
  const lastExtractedTextRef = useRef<string>("");
  const lastFetchedContentTextKeyRef = useRef<string>("");
  const outputTextRequestRef = useRef<Promise<void> | null>(null);

  // ✅ HARD RESET on modal open to avoid showing previous run's state
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    if (open && !wasOpen) {
      // Clear UI state for a brand-new session
      setSteps([]);
      setProgress(0);
      setError("");
      setRawError("");
      setCurrentStep("");
      setIsUploading(false);
      setIsCompleted(false);
      setCompletionResult("");
      setOutputFiles([]);
      setDownloadLoading(false);
      setStateCleared(false);
      setMarkdownContent("");
      setStreamingContent("");
      setFallbackTransformText("");
      setShowMarkdownPreview(false);
      setActiveTab(0);
      setThinkingText('');
      setShowThinking(false);
      setDocumentProcessingText('');
      setShowDocumentProcessing(false);
      setCurrentProcessingPhase('');
      setIsAtBottom(true);
      setDocumentSections(new Map());
      setIsStreamingActive(false);

      // Clear working refs and buffers
      stepsRef.current = [];
      uploadStateRef.current = { hasStarted: false, jobId: undefined, attemptCount: 0 };
      currentJobIdRef.current = jobId || undefined;
      lastCompletedJobRef.current = undefined;
      completeNotifiedJobRef.current = undefined;
      chunkBuffers.current.clear();
      processStartedRef.current = false;
      lastExtractedTextRef.current = "";
      lastFetchedContentTextKeyRef.current = "";
      outputTextRequestRef.current = null;
      lastDownloadTargetRef.current = '';
    }
    prevOpenRef.current = open;
  }, [open, jobId]);

  // ✅ NEW: Reset state for new jobs or fresh starts
  useEffect(() => {
    // Detect when this is a genuinely new job OR a fresh start
    const isNewJob = open && jobId && jobId !== currentJobIdRef.current;
    const isDifferentFromLastCompleted = jobId !== lastCompletedJobRef.current;
    const isFreshStart = open && files.length > 0 && (!jobId || jobId === '') && currentJobIdRef.current;
    // Do NOT treat an error state as stale; we want to keep the error visible
    const isStaleState = open && files.length > 0 && isCompleted && (!jobId || jobId === '');
    
    if ((isNewJob && isDifferentFromLastCompleted) || isFreshStart || isStaleState) {
      console.log('🔄 Reset trigger detected, resetting modal state. Old:', currentJobIdRef.current, 'New:', jobId || 'empty', 'Fresh start:', isFreshStart, 'Stale state:', isStaleState);
      
      // Reset all modal state for new job
      setSteps([]);
      setProgress(0);
      setCurrentStep("");
      // Preserve any existing error; only clear on intentional reset cases
      if (!error) {
        setError("");
        setRawError("");
      }
      setIsUploading(false);
      setIsCompleted(false);
      setCompletionResult("");
      setOutputFiles([]);
      setDownloadLoading(false);
      setStateCleared(false);
      setMarkdownContent("");
      setStreamingContent("");
      setFallbackTransformText("");
      setShowMarkdownPreview(false);
      setActiveTab(0);
      setThinkingText('');
      setShowThinking(false);
      setDocumentProcessingText('');
      setShowDocumentProcessing(false);
      setCurrentProcessingPhase('');
      setIsAtBottom(true);
      setDocumentSections(new Map());
      lastExtractedTextRef.current = "";
      setIsStreamingActive(false);
      
      // Clear chunk buffers
      chunkBuffers.current.clear();
      
      // Clear refs
      stepsRef.current = [];
      uploadStateRef.current = {
        hasStarted: false,
        jobId: undefined,
        attemptCount: 0
      };
      
      // Update current job tracking and clear completed job tracking
      currentJobIdRef.current = jobId || undefined;
      lastCompletedJobRef.current = undefined;
      completeNotifiedJobRef.current = undefined;
      lastFetchedContentTextKeyRef.current = "";
      outputTextRequestRef.current = null;
      lastDownloadTargetRef.current = '';
      
      console.log('✅ Modal state reset complete for job:', jobId || 'fresh start');
    }
  }, [open, jobId, files.length, isCompleted, error]);

  const hydrateOutputText = useCallback(async (status: Pick<JobStatusResponse, "jobId" | "metadata">) => {
    const contentTextKey = typeof status.metadata?.contentTextKey === "string"
      ? status.metadata.contentTextKey.trim()
      : "";
    if (!status.jobId || !contentTextKey) {
      return;
    }

    if (contentTextKey === lastFetchedContentTextKeyRef.current || outputTextRequestRef.current) {
      return;
    }

    outputTextRequestRef.current = (async () => {
      try {
        const result = await getJobOutputText(status.jobId);
        if (!result.text) {
          return;
        }

        lastFetchedContentTextKeyRef.current = contentTextKey;
        setFallbackTransformText(result.text);
      } catch (error) {
        console.warn("Failed to fetch job output text:", error);
      } finally {
        outputTextRequestRef.current = null;
      }
    })();

    await outputTextRequestRef.current;
  }, []);

  useEffect(() => {
    if (!open || !onTextExtracted) {
      return;
    }

    const previewText = (streamingContent || markdownContent || fallbackTransformText || "").trim();
    if (!previewText || previewText === lastExtractedTextRef.current) {
      return;
    }

    const extractedText = extractTransformableText(previewText);
    if (!extractedText) {
      return;
    }

    lastExtractedTextRef.current = previewText;
    onTextExtracted(extractedText);
  }, [open, streamingContent, markdownContent, fallbackTransformText, onTextExtracted]);

  useEffect(() => {
    if (!onStreamingStateChange) {
      return;
    }

    onStreamingStateChange(Boolean(open && isStreamingActive));

    return () => {
      onStreamingStateChange(false);
    };
  }, [open, isStreamingActive, onStreamingStateChange]);

  useEffect(() => {
    if (!open || !isCompleted || !onDownloadReady) {
      return;
    }

    const primaryDownloadTarget = outputFiles[0]?.key || completionResult.trim();
    if (
      !primaryDownloadTarget ||
      primaryDownloadTarget === lastDownloadTargetRef.current ||
      /^processing completed/i.test(primaryDownloadTarget)
    ) {
      return;
    }

    lastDownloadTargetRef.current = primaryDownloadTarget;
    onDownloadReady(primaryDownloadTarget);
  }, [open, isCompleted, outputFiles, completionResult, onDownloadReady]);

  // Keep job tracking aligned with modal lifecycle.
  useEffect(() => {
    if (open && jobId && userId) {
      console.log('UploadLoaderModal opened with job:', jobId);
    } else if (!open) {
      console.log('UploadLoaderModal closing - resetting job tracking');
      currentJobIdRef.current = undefined;
      processStartedRef.current = false;
    }
  }, [open, jobId, userId]);

  // ✅ NEW: Function to update state from backend status response
  const updateFromBackendStatus = useCallback((status: JobStatusResponse) => {
    console.log('📊 Updating from backend status:', status);
    
    // Update progress
    if (status.progress !== undefined) {
      setProgress(status.progress);
    }
    
    const latestStepLabel = status.stepHistory && status.stepHistory.length > 0
      ? status.stepHistory[status.stepHistory.length - 1]?.label
      : undefined;
    const reportedStep = status.currentStep || latestStepLabel;
    if (reportedStep) {
      setCurrentStep(reportedStep);
    }

    // Update steps from backend stepHistory if available
    if (status.stepHistory && status.stepHistory.length > 0) {
      const convertedSteps = convertBackendStepsToFrontend(status.stepHistory, true);
      updateStepsState(convertedSteps, 'Updated from backend stepHistory (in progress)');
    } else if (status.currentStep) {
      // Fallback to currentStep if no stepHistory
      addOrUpdateStep({
        label: toSentenceCase(status.currentStep),
        status: status.status === 'COMPLETED' ? 'completed' : 'processing'
      });
    }
  }, [updateStepsState, addOrUpdateStep]);

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, []);

  // Function to check if user is at bottom
  const checkIfAtBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 50; // 50px threshold for "near bottom"
      const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      setIsAtBottom(atBottom);
    }
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkIfAtBottom();
  }, [checkIfAtBottom]);

  // Auto-scroll when steps change (only if user is at bottom)
  useEffect(() => {
    if (isAtBottom && steps.length > 0) {
      // Small delay to ensure DOM has updated
      setTimeout(scrollToBottom, 100);
    }
  }, [steps, isAtBottom, scrollToBottom]);

  // Get auth token for Pusher
  const getAuthToken = useCallback(async () => {
    const authHeader = await getAuthHeader();
    return authHeader.Authorization?.replace('Bearer ', '') || '';
  }, []);

  // Handle downloading with on-demand URL generation
  const handleDownload = async (fileKey: string) => {
    if (downloadLoading) return; // Prevent double-clicks
    
    setDownloadLoading(true);
    
    try {
      // `fileKey` may be a bare S3 key OR a (possibly-expired) presigned URL from the
      // realtime completion push — downloadOutput extracts the key and re-signs fresh
      // at click time, so the link never expires mid-session (Trello #404).
      await downloadOutput(fileKey);
    } catch (err: any) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to download file.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleJobStatusUpdate = useCallback(async (update: JobStatusUpdate) => {
    if (!jobId || update.jobId !== jobId) {
      console.log(`Ignoring update for job ${update.jobId}, current job is ${jobId}`);
      return;
    }

    console.log('📡 Realtime job status update received in modal', {
      jobId: update.jobId,
      status: update.status,
      currentStep: update.currentStep,
      isStreamingUpdate: update.isStreamingUpdate,
      hasStreamingContent: !!update.streamingContent,
    });

    // ✅ ENTERPRISE SOLUTION: Remove persistence responsibility from this function
    // Persistence will be handled by a separate, independent effect

    // Handle thinking updates
    if (update.isThinkingUpdate && update.thinkingText) {
      setThinkingText(prev => prev + update.thinkingText);
      setShowThinking(true);
      return; // Don't process as regular step update
    }

    // Handle document processing updates
    if (update.isDocumentProcessingUpdate && update.processingText) {
      setDocumentProcessingText(update.processingText);
      setShowDocumentProcessing(true);
      if (update.currentPhase) {
        setCurrentProcessingPhase(update.currentPhase);
      }
      
      // Check if this is a completion update
      if (update.operationStatus === 'COMPLETED') {
        // Hide document processing section after a brief delay to show completion message
        setTimeout(() => {
          setShowDocumentProcessing(false);
        }, 2000);
      }
      
      // Don't return here - we want this to also update the regular progress
    }

    // Handle streaming chunk updates
    if (update.isStreamingChunk && update.streamingChunk) {
      const chunk = update.streamingChunk;
      const buffer = chunkBuffers.current.get(chunk.chunkId) || { 
        chunks: new Array(chunk.totalChunks), 
        totalChunks: chunk.totalChunks 
      };
      
      // Store the chunk in its correct position
      buffer.chunks[chunk.chunkIndex] = chunk;
      chunkBuffers.current.set(chunk.chunkId, buffer);
      
      // Check if all chunks are received
      const allChunksReceived = buffer.chunks.every(c => c !== undefined);
      if (allChunksReceived) {
        // Assemble the complete content
        const assembledContent = (Array.isArray(buffer.chunks) ? buffer.chunks : []).map(c => c.content).join('');
        
        if (chunk.section === 'COMPLETE_DOCUMENT') {
          setStreamingContent(assembledContent);
        } else if (chunk.section) {
          // Update specific section
          setDocumentSections(prev => {
            const newSections = new Map(prev);
            newSections.set(chunk.section!, assembledContent);
            return newSections;
          });
        }
        
        // Clean up the buffer
        chunkBuffers.current.delete(chunk.chunkId);
        
        // Mark streaming active so the inline panel defers to the tool's output panel.
        setIsStreamingActive(true);
      }
      return;
    }

    // Handle streaming content updates (non-chunked)
    if (update.isStreamingUpdate && update.streamingContent) {
      if (update.streamingSection === 'COMPLETE_DOCUMENT') {
        setStreamingContent(prev => prev + update.streamingContent!);
      } else if (update.streamingSection) {
        // Update specific section
        setDocumentSections(prev => {
          const newSections = new Map(prev);
          newSections.set(update.streamingSection!, update.streamingContent!);
          return newSections;
        });
      } else {
        // Append general content
        setStreamingContent(prev => prev + '\n\n' + update.streamingContent);
      }
      
      // Mark streaming active so the inline panel defers to the tool's output panel.
      setIsStreamingActive(true);
      return;
    }

    // Handle old-style markdown preview updates (backward compatibility)
    if ((update as any).isMarkdownPreview && (update as any).markdownContent) {
      setMarkdownContent((update as any).markdownContent);
      setShowMarkdownPreview(true);
      return; // Don't process as regular step update
    }

    if (update.currentStep) {
      setCurrentStep(update.currentStep);
      // Use centralized step management
      addOrUpdateStep({
        label: toSentenceCase(update.currentStep),
        status: update.status === 'COMPLETED' ? 'completed' : 'processing'
      });
    }

    // Update progress if provided
    if (update.progress !== undefined) {
      setProgress(update.progress);
    }
  }, [jobId, addOrUpdateStep]);

  const handleJobCompleted = useCallback(async ({ jobId: completedJobId, update, status }: RealtimeJobCompletionPayload) => {
    if (!jobId || completedJobId !== jobId) {
      return;
    }

    setIsUploading(false);
    setIsCompleted(true);
    setShowThinking(false);
    setShowDocumentProcessing(false);
    setIsStreamingActive(false);
    setProgress(100);

    const completionOutput =
      update?.outputLocation
      || status?.outputLocation
      || "Processing completed successfully";
    setCompletionResult(completionOutput);

    const payloadOutputFiles = Array.isArray((update as any)?.outputFiles) && (update as any).outputFiles.length > 0
      ? (update as any).outputFiles
      : Array.isArray((status as any)?.outputFiles) && (status as any).outputFiles.length > 0
        ? (status as any).outputFiles
        : null;
    if (payloadOutputFiles) {
      setOutputFiles(payloadOutputFiles);
    }

    if (status) {
      void hydrateOutputText(status);
    } else {
      void getJobStatus(completedJobId)
        .then((nextStatus) => {
          if (nextStatus) {
            return hydrateOutputText(nextStatus);
          }
          return undefined;
        })
        .catch((completionError) => {
          console.warn('Failed to re-hydrate completed job output text:', completionError);
        });
    }

    if (jobId && userId) {
      try {
        await setUserActiveJob({
          jobId,
          jobType: detectJobTypeFromPath(window.location.pathname) || JobType.MEMO,
          status: 'COMPLETED',
          progress: 100,
          currentStep: 'Completed'
        });
      } catch (completionError) {
        console.error('Failed to mark job as completed:', completionError);
      }
    }

    lastCompletedJobRef.current = completedJobId;
    setTimeout(() => {
      setStateCleared(true);
    }, 1000);

    try {
      await Promise.resolve(refreshUsage());
    } catch {}

    setSteps((prevSteps) =>
      prevSteps.map((step) => ({
        ...step,
        status: 'completed' as const,
        timestamp: step.timestamp || new Date().toLocaleTimeString(),
      })),
    );

    // #611 — tell the parent the job is done. Before this, `onComplete` was only
    // ever called on the ANONYMOUS upload path (`!userId`, in the upload effect
    // below), so for a signed-in user it never fired at all. Every consumer uses
    // it to clear `uploadForm.processing`, which left that flag stuck `true`
    // after every job — disabling the "＋ New recording" / "＋ New audio file"
    // reset buttons and freezing the settings panel until a page refresh.
    // Guarded so it runs at most once per job (consumers make network calls).
    if (completeNotifiedJobRef.current !== completedJobId) {
      completeNotifiedJobRef.current = completedJobId;
      try {
        await Promise.resolve(onComplete(completionOutput));
      } catch (notifyError) {
        console.error('onComplete handler threw after job completion:', notifyError);
      }
    }
  }, [hydrateOutputText, jobId, onComplete, refreshUsage, userId]);

  const handleJobFailed = useCallback(async ({ jobId: failedJobId, error: failureError }: RealtimeJobFailurePayload) => {
    if (!jobId || failedJobId !== jobId) {
      return;
    }

    setError(enhanceErrorMessage(failureError || "Processing failed"));
    setRawError(failureError || "");
    setIsUploading(false);
    setShowThinking(false);
    setShowDocumentProcessing(false);
    setIsStreamingActive(false);

    if (jobId && userId) {
      try {
        await setUserActiveJob({
          jobId,
          jobType: detectJobTypeFromPath(window.location.pathname) || JobType.MEMO,
          status: 'FAILED',
          progress: 0,
          currentStep: 'Failed'
        });
      } catch (failurePersistError) {
        console.error('Failed to mark job as failed:', failurePersistError);
      }
    }
  }, [jobId, userId]);

  const realtimeJob = useRealtimeJob({
    jobId: open ? jobId || null : null,
    userId: open ? userId || null : null,
    getAuthToken,
    enabled: Boolean(open && jobId && userId && !isCompleted && !error),
    pollIntervalMs: FALLBACK_POLL_INTERVAL_MS,
    realtimeGracePeriodMs: PUSHER_TIMEOUT_MS,
    initialStatusCheck: true,
    onUpdate: handleJobStatusUpdate,
    onStatus: updateFromBackendStatus,
    onCompleted: handleJobCompleted,
    onFailed: handleJobFailed,
  });

  const pusherConnectionState: 'connecting' | 'connected' | 'disconnected' | 'failed' =
    realtimeJob.connectionState === 'connected'
      ? 'connected'
      : realtimeJob.connectionState === 'polling'
        ? 'failed'
      : realtimeJob.connectionState === 'failed'
        ? 'failed'
        : realtimeJob.connectionState === 'idle'
          ? 'disconnected'
          : 'connecting';
  const fallbackPollingActive = realtimeJob.isPollingFallback;

  // ✅ FINAL SOLUTION: Move persistence to parent component or context
  // This component should only handle UI state, not persistence
  // ResumeJobNotification issue needs to be solved at the architecture level

  // Handle the upload process
  useEffect(() => {
    // ✅ CRITICAL: Add multiple safeguards to prevent re-triggering
    
    // Guard - single run per modal open session
    if (processStartedRef.current) {
      return;
    }

    // Guard 0: Check if upload already started for this specific job (CRITICAL for preventing loops)
    if (uploadStateRef.current.hasStarted && uploadStateRef.current.jobId === jobId) {
      console.log('⛔ Upload blocked - already started for job:', jobId);
      return;
    }
    
    // Guard 1: Don't run if already uploading, completed, or has error (no automatic retries)
    if (isUploading || isCompleted || error) {
      console.log('⛔ Upload blocked - already in progress/completed/failed (no automatic retries)');
      return;
    }
    
    // Guard 2: Skip if this appears to be a resumed job (no files but have jobId)
    if (jobId && files.length === 0) {
      console.log('⛔ Resumed job detected (no files), skipping upload logic');
      return;
    }
    
    // Guard 3: Modal must be open
    if (!open) {
      console.log('⛔ Upload blocked - modal closed');
      return;
    }
    
    // Guard 4: Prevent multiple uploads for same job
    if (jobId && lastCompletedJobRef.current === jobId) {
      console.log('⛔ Upload blocked - job already completed');
      return;
    }

 // ✅ CRITICAL: Mark upload as started for this specific job
    uploadStateRef.current = {
      hasStarted: true,
      jobId: jobId,
      attemptCount: uploadStateRef.current.attemptCount + 1
    };
    processStartedRef.current = true;

    console.log('✅ Starting upload process for new job with', files.length, 'files');
    setIsUploading(true);
    setProgress(0);
    setError("");
    
    const initialStep: ProcessingStep = {
      id: 'upload-files',
      label: files.length > 0 ? 'Uploading files...' : 'Processing...',
      status: 'processing',
      timestamp: undefined
    };
    updateStepsState([initialStep], 'Initial upload step');
    
    // ✅ Initial persistence will be handled by separate effect

    const processUpload = async () => {
      try {
        await onUpload(files, (uploadProgress) => {
          setProgress(uploadProgress);
          
          // Update upload step progress
          if (uploadProgress === 100) {
            const completedStep: ProcessingStep = {
              id: 'upload-files',
              label: 'Uploading files...',
              status: 'completed',
              timestamp: new Date().toLocaleTimeString()
            };
            updateStepsState([completedStep], 'Upload step completed');
          }
        });

        // Refresh usage after successful job creation (upload completed)
        try { await Promise.resolve(refreshUsage()); } catch {}

        // If userId is provided, we expect processing phase after upload
        // If no userId provided, complete immediately after upload  
        if (!userId || userId === '') {
          // Mark all steps as completed for non-authenticated flow
          const currentSteps = stepsRef.current.map(step => ({ 
            ...step, 
            status: 'completed' as const,
            timestamp: new Date().toLocaleTimeString()
          }));
          updateStepsState(currentSteps, 'Upload completed (no auth)');
          onComplete("Upload completed successfully");
        } else {
          // Upload done, transition to processing phase
          setIsUploading(false);
          
          // ✅ NEW: Set active job in database
          if (jobId && userId) {
            const jobType = detectJobTypeFromPath(window.location.pathname) || JobType.MEMO;
            try {
              await setUserActiveJob({
                jobId,
                jobType,
                status: 'PROCESSING',
                progress: 100,
                currentStep: 'Processing documents...'
              });
            } catch (error) {
              console.error('Failed to set active job:', error);
              // Continue anyway - this is not critical to upload success
            }
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Upload failed";
        
        console.error("❌ Upload failed:", errorMessage);
        
        // Fail-fast: Show error and require manual resubmission
        setError(`${errorMessage}. Please close this dialog and try again, or contact support if the issue persists.`);
        setRawError(errorMessage);
        setIsUploading(false);
        // Do NOT reset hasStarted here; keep it true to prevent any auto-retry within the same modal session.
        // Retry requires closing the modal and starting a new session.
      }
    };

    processUpload();
  }, [files, open, jobId, userId, isUploading, isCompleted, error, onUpload, onComplete, updateStepsState, refreshUsage]);

  // ✅ Track global processing modal state
  useEffect(() => {
    setProcessingModalOpen(variant === 'modal' ? open : false);
  }, [open, setProcessingModalOpen, variant]);

  // 🚨 CTO CRITICAL FIX: Component unmount cleanup (industry standard)
  useEffect(() => {
    return () => {
      console.log('🧹 UploadLoaderModal unmounting - executing cleanup strategy');
      console.log('Component unmounting for job:', jobId);
    };
  }, [jobId]);

  // ✅ SIMPLIFIED: Modal close handler
  const handleModalClose = useCallback(() => {
    // Call parent's onClose handler
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Limit the overall modal height (modal variant), but apply the provided maxHeight
  // only to the scrollable body content (list/preview area) so header/footer are excluded.
  const containerMaxHeight = variant === 'inline' ? 'none' : '90vh';

  const inlineCardStyles = variant === 'inline'
    ? {
        bgcolor: 'rgba(10, 18, 28, 0.78)',
        border: '1px solid rgba(120, 255, 214, 0.18)',
        boxShadow: '0 18px 40px rgba(0, 0, 0, 0.32)',
        backdropFilter: 'blur(18px)',
      }
    : {};

  const hasInlineResultSurface = variant === 'inline' && (!!onTextExtracted || !!onDownloadReady);
  const shouldHideCompletedInlinePanel = hasInlineResultSurface
    && !error
    && (isCompleted || isStreamingActive || !!streamingContent || showMarkdownPreview);
  const shouldCollapseCompletedContent = false;

  const content = (
    <Box
      sx={{
        // Dynamic width based on tab selection and screen size
        width: variant === 'inline' ? '100%' : { xs: "95vw", sm: 600, md: 720, lg: activeTab === 1 ? "80%" : 720 },
        maxWidth: variant === 'inline' ? '100%' : (activeTab === 1 ? "1200px" : "720px"),
        height: variant === 'inline' ? 'auto' : (activeTab === 1 ? '85vh' : 'auto'),
        maxHeight: containerMaxHeight,
        bgcolor: "background.paper",
        borderRadius: variant === 'inline' ? '22px' : 2,
        overflow: "hidden",
        boxShadow: variant === 'inline' ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        ...(variant === 'inline' ? {} : {}),
        position: 'relative',
        transition: 'width 0.3s ease-in-out, max-width 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        ...inlineCardStyles
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          position: variant === 'inline' ? 'relative' : 'sticky',
          top: 0,
          zIndex: 3,
          bgcolor: variant === 'inline' ? 'transparent' : 'background.paper',
          borderBottom: variant === 'inline' ? '1px solid rgba(140, 160, 180, 0.12)' : '1px solid #e5e7eb',
        }}
      >
        <ModalHeader  
          error={error}
          isCompleted={isCompleted}
          stateCleared={stateCleared}
          progress={progress}
          pusherConnectionState={pusherConnectionState}
          fallbackPollingActive={fallbackPollingActive}
          jobId={jobId}
          onClose={onClose}
          onCloseClick={() => {
            onClose && onClose();
          }}
          theme={theme}
        />
        {/* Remove inline header error alert to avoid duplication */}
      </Box>

      {/* Main Content */}
      {!(isCompleted && hideStatusUpdatesOnCompleted) && !shouldCollapseCompletedContent && (
        <Box
          sx={{
            bgcolor: variant === 'inline' ? 'transparent' : 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Tabs only appear for the premium Document Chat (post-completion).
              The live preview tab was removed; progress is the only in-modal view. */}
          {(isCompleted && chatEnabled && hasDocumentChatAccess && !!documentTitle) && (
            <ModalTabs
              activeTab={activeTab}
              onTabChange={(event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue)}
              showChatTab
            />
          )}

          {/* Tab Content */}
          <Box 
            sx={{ 
              flex: 1,
              bgcolor: variant === 'inline' ? 'transparent' : 'background.paper',
              // Apply the consumer-provided maxHeight to the scrollable body only
              // so header and footer are not counted in the height.
              ...(typeof maxHeight !== 'undefined' ? { maxHeight } : {}),
              overflowY: activeTab === 1 ? 'hidden' : 'auto',
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: 3,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                }
              }
            }}
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            {/* Processing Steps — the only in-modal view (shown unless the Document Chat tab is active) */}
            {activeTab === 0 && (
              <ProcessingStepsTab
                steps={steps}
                showThinking={showThinking}
                thinkingText={thinkingText}
                showDocumentProcessing={showDocumentProcessing}
                documentProcessingText={documentProcessingText}
                currentProcessingPhase={currentProcessingPhase}
                error={error}
              />
            )}

            {/* Document Chat Tab */}
            {isCompleted && chatEnabled && hasDocumentChatAccess && !!documentTitle && activeTab === 1 && (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <DocumentChatContent
                  sourceJobId={jobId || ''}
                  documentTitle={documentTitle}
                  showHeader={false}
                  embedded={true}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Footer Section */}
      {isCompleted && chatEnabled && hasDocumentChatAccess && !!documentTitle && activeTab === 1 ? null : (
        typeof renderFooter === 'function' ? (
          renderFooter({
            isCompleted,
            downloadLoading,
            stateCleared,
            completionResult,
            error,
            onDownload: handleDownload,
            onClose: handleModalClose,
            jobId,
            userId,
            files
          })
        ) : (
          <CompletionFooter
            isCompleted={isCompleted}
            downloadLoading={downloadLoading}
            stateCleared={stateCleared}
            completionResult={completionResult}
            outputFiles={outputFiles.length > 0 ? outputFiles : undefined}
            error={error}
            onDownload={handleDownload}
            onClose={() => {
              if (onClose) {
                onClose();
              }
            }}
          />
        )
      )}
    </Box>
  );

  if (variant === 'inline') {
    if (!open) return null;
    if (shouldHideCompletedInlinePanel) return null;
    if (presentation === 'compact') {
      // A finished job has nothing left to show here (the tool's own surface takes over); a failed or
      // running job shows one bar and one plain line. All tracking above is shared with 'steps'.
      if (isCompleted && !error) return null;
      return (
        <CompactProcessingIndicator
          status={error ? 'FAILED' : 'PROCESSING'}
          progress={progress}
          currentStep={isUploading ? 'upload' : currentStep}
          errorMessage={rawError}
          onRetry={onClose ? handleModalClose : undefined}
        />
      );
    }
    return content;
  }

  return (
    <Modal 
      open={open} 
      onClose={isCompleted || error ? handleModalClose : undefined}
      keepMounted
      disableEscapeKeyDown
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Fade in={open} timeout={300}>
        {content}
      </Fade>
    </Modal>
  );
};

export default UploadLoaderModal;
